import { useState } from 'react';
import styled from 'styled-components';
import { Flex, StatusChip } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import {
  fontSize,
  fontWeight,
  space,
  lineHeight,
} from '@elementary/common-ui/style/utils';
import { colorTokens } from '@elementary/common-ui/style/tokens';
import { ThresholdLevel } from '../../types';
import { getValueColor } from '../../utils';
import ContentWithLabel from '../shared/ContentWithLabel';
import SegmentedLineChart from '../shared/SegmentedLineChart';
import ScoreFormulaDialog from './ScoreFormulaDialog';

const colors = [
  colorTokens.status.error.soft,
  colorTokens.status.warning.strong,
  colorTokens.status.success.main,
];

const ValueWrapper = styled(Flex)`
  gap: ${space('lg')};
  align-items: flex-end;
  margin-bottom: ${space('xl')};
`;

const Value = styled(Typography)<{ $color: string }>`
  font-size: ${fontSize('displayXl')};
  font-weight: ${fontWeight('medium')};
  line-height: ${lineHeight('display')};
  color: ${({ $color }) => $color};
`;

type Props = {
  value: number;
  thresholdLevel: ThresholdLevel;
  thresholds: {
    low: number;
    medium: number;
  };
  onTotalHealthScoreFormulaChange: () => void;
};

const TotalHealthScore = ({
  value,
  thresholdLevel,
  thresholds,
  onTotalHealthScoreFormulaChange,
}: Props) => {
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  const numberColor = getValueColor(thresholdLevel);

  return (
    <ContentWithLabel
      variant="big"
      title="Total Health Score"
      openSettings={() => setIsSettingsDialogOpen(true)}
    >
      <ValueWrapper>
        <Value $color={numberColor}>{Math.round(value)}%</Value>

        {thresholdLevel !== null && <StatusChip status={thresholdLevel} />}
      </ValueWrapper>

      <SegmentedLineChart
        segmentsColors={colors}
        segmentsValues={[
          thresholds.low,
          thresholds.medium - thresholds.low,
          100 - thresholds.medium,
        ]}
        markerValue={value}
      />

      {isSettingsDialogOpen && (
        <ScoreFormulaDialog
          isOpen={isSettingsDialogOpen}
          onClose={() => {
            setIsSettingsDialogOpen(false);
          }}
          onSave={() => {
            onTotalHealthScoreFormulaChange();
            setIsSettingsDialogOpen(false);
          }}
        />
      )}
    </ContentWithLabel>
  );
};

export default TotalHealthScore;
